import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OwnershipLocalState } from '../../model/book-model';
import { map, Observable, of, Subscription, switchMap, tap, last, finalize, delay, shareReplay } from 'rxjs';
import { NativeServiceApiProvider } from 'src/app/services/native-api-provider.service';
import { LogsService } from 'src/app/utils/services/logs.service';
import { BookDetailsService } from '../../services/book-details.service';
import { LangCode } from 'src/app/utils/text-compare';
import { AuthorizationServiceProvider } from 'src/app/auth_profile/services/authorization-service.provider';
import { CourseFilter } from 'src/app/model/CourseFilters';
import { environment } from 'src/environments/environment';
import { PlatformDescriptor } from 'src/app/utils/platform-descriptor';
import { BookListEntry } from '../../model/v3/drm';

@Component({
    selector: 'app-book-details',
    templateUrl: './book-details.component.html',
    styleUrls: ['./book-details.component.scss'],
    standalone: false
})
export class BookDetailsComponent implements OnInit {
  ebookId: string
  entry: BookListEntry
  hasProductFlag = false
  usedDevices = 0
  actionDisabled = false;
  isImgLoading = true
  isContentLoading = true
  ebookCover = null
  audioDisabled = false;
  dictDisabled = false;
  downloadProgress: number = null;
  audioProgress: number = null;
  dictProgress: number = null;
  localState: OwnershipLocalState;
  updatesSubscription: Subscription;
  isAppNative: boolean
  private refreshInFlight$: Observable<any> | null = null;

  constructor(
    private route: ActivatedRoute,
    private apiProvider: NativeServiceApiProvider,
    private router: Router,
    private logger: LogsService,
    public bookDetails: BookDetailsService,
    private authServiceProv: AuthorizationServiceProvider
    ) {
      this.authServiceProv.getCommonRoutines().subscribe(auth =>
        this.isAppNative = auth.isNativeAuthorization()
      )
    }

  ngOnInit(): void {
      this.route.paramMap.pipe(
        tap(params => {
          this.ebookId = params.get("ebookId");
        }),
        switchMap(_ => this.refreshBookState()),
        delay(100),
        tap(_ => this.traceDownloadableContentProgress())
      ).subscribe()

      this.apiProvider.books().pipe(
        map( api => api.listenForDataUpdates())
      ).subscribe(emitter =>  {
        this.updatesSubscription = emitter.subscribe(() => this.refreshBookState().subscribe())
      });
  }

  ngOnDestroy(): void {
    this.updatesSubscription.unsubscribe();
  }

  traceDownloadableContentProgress() {
    if (!this.localState) return;
    this.apiProvider.books().subscribe(
      api => {
        this.attachDictProgress(api.downloadingDictationProgress(+this.ebookId, this.localState.ownership.eBook.release.id))
        this.attachAudioProgress(api.downloadingAudioProgress(+this.ebookId, this.localState.ownership.eBook.release.id))
      }
    )

  }

  attachDictProgress(progressEmitter: Observable<number>) {
    this.dictDisabled = true;
    this.dictProgress = 0;
    this.doWithProgress(
        progressEmitter,
        (progress) => this.dictProgress = progress,
        () => {
          this.dictDisabled = false;
          this.dictProgress = null;
        }
    );
  }

  attachAudioProgress(progressEmitter: Observable<number>) {
    this.audioDisabled = true;
    this.audioProgress = 0;
    this.doWithProgress(
        progressEmitter,
        progress => {
          this.logger.log(`got audio progress report ${progress}`)
          this.audioProgress = progress;
        },
        () => {
          this.audioDisabled = false;
          this.audioProgress = null;
        });
  }

  doWithProgress(
    progressObservable: Observable<number>,
    progressHandler: (progress: number) => void, completeCallback: () => void) {

      progressObservable.pipe(
        finalize(() => {
          completeCallback();
          this.refreshBookState().subscribe();
        })
      ).subscribe(progress =>
        progressHandler(progress)
      );
  }

  private refreshBookState(): Observable<any> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }
    this.refreshInFlight$ = this.loadBook().pipe(
      finalize(() => {
        this.refreshInFlight$ = null;
      }),
      shareReplay(1)
    );
    return this.refreshInFlight$;
  }

  loadBook(): Observable<any> {
    return this.apiProvider.booksV3().pipe(
      switchMap( api =>
        api.getProductById(+this.ebookId).pipe(
          tap(entry => {
            this.entry = entry
            this.usedDevices = entry.usedDevices ?? 0
            this.hasProductFlag = entry.hasProduct ?? false
            this.isContentLoading = false
          }),
          switchMap( entry => api.getImageUrl(+this.ebookId, entry.releaseId)),
          tap(data => {
            this.ebookCover = data;
            this.isImgLoading = false
          }),
          switchMap( _ => this.loadNativeLocalState())
      )
    ))
  }

  private loadNativeLocalState(): Observable<any> {
    if (environment.platform.name === PlatformDescriptor.web.name) {
      return of(null)
    }
    return this.apiProvider.books().pipe(
      switchMap( api => api.getProductById(+this.ebookId)),
      tap(ownershipLS => this.localState = ownershipLS)
    )
  }

  onAction() {
    const action = this.bookDetails.getAction(this.localState);

    if (action === this.bookDetails.ACTION_REGISTER_DOWNLOAD || action === this.bookDetails.ACTION_DOWNLOAD) {
      this.actionDisabled = true;
      this.downloadProgress = 0;
      this.apiProvider.books().pipe(
        switchMap( api =>
          api.register(+this.ebookId, this.localState.ownership.eBook.release.id).pipe(
            switchMap( () => api.download(+this.ebookId, this.localState.ownership.eBook.release.id)),
            tap( progress => this.downloadProgress = progress as number),
            last(),
            switchMap( () => this.refreshBookState()),
            switchMap( () => api.open(+this.ebookId, this.localState.ownership.eBook.release.id)),
            finalize(() => {
              this.logger.log('on finalize');
              this.actionDisabled = false;
              this.downloadProgress = null;
            }),
        )
      )
      ).subscribe(_ =>
          this.logger.log('got some response from download event')
      );
    } else if ( action === this.bookDetails.ACTION_OPEN) {
      this.actionDisabled = true;
      this.apiProvider.books().pipe(
        switchMap( api => api.open(+this.ebookId, this.localState.ownership.eBook.release.id))
      ).subscribe( _ =>
        this.actionDisabled = false
      );
    }
  }

  downloadDict() {
    if (this.dictDisabled) {
      return;
    }

    this.attachDictProgress(
      this.apiProvider.books().pipe(
        switchMap( api => api.downloadDict(+this.ebookId, this.localState.ownership.eBook.release.id))
      ));
  }

  deleteDict() {
    if (this.dictDisabled) {
      return;
    }

    this.dictDisabled = true;
    this.doWithProgress(
      this.apiProvider.books().pipe(
        switchMap( api => api.deleteDict(+this.ebookId, this.localState.ownership.eBook.release.id))
      ),
      () => {},
      () => this.dictDisabled = false
    );
  }

  open() {
    const action = this.bookDetails.getAction(this.localState);
    if ( action === this.bookDetails.ACTION_OPEN) {
      this.actionDisabled = true;
      this.apiProvider.books().pipe(
        switchMap(api => api.open(+this.ebookId, this.localState.ownership.eBook.release.id))
      ).subscribe(_ =>
        this.actionDisabled = false
      );
    }
  }

  downloadAudio() {
    if (this.audioDisabled) {
      return;
    }
    this.attachAudioProgress(
      this.apiProvider.books().pipe(
        switchMap(api => api.downloadAudio(+this.ebookId, this.localState.ownership.eBook.release.id))
      ));
  }

  deleteAudio() {
    if (this.audioDisabled) {
      return;
    }

    this.audioDisabled = true;
    this.doWithProgress(
      this.apiProvider.books().pipe(
        switchMap( api => api.deleteAudio(+this.ebookId, this.localState.ownership.eBook.release.id))
      ),
      () => {},
      () => this.audioDisabled = false
    );
  }

  getShares() {
    return {
      used: this.usedDevices,
      total: this.usedDevices + (this.entry?.share.emptySlots ?? 0)
    }
  }

  getActionName() {
    if (!this.entry) return null;
    else if(this.bookDetails.getAction(this.localState) === this.bookDetails.ACTION_REGISTER_DOWNLOAD)
      return 'EBOOKS.ACTION.' + 'REGISTER_DOWNLOAD'
    return 'EBOOKS.ACTION.' + this.bookDetails.getAction(this.localState).toUpperCase()
  }

  private product() {
    return this.entry?.share.product
  }

  getAccent() {
    const name = this.product()?.name
    return `stg-${name?.slice(name.length - 2).trim()}`
  }

  getEbookName() {
    return this.product()?.name
  }

  getEbookLanguage() {
    return this.product()?.categories ? this.product()?.categories[0] : ''
  }
  getEbookLanguageTranslation() {
    return this.product() && this.product().categories ? 'EBOOKS.FILTER_LANGS.' + LangCode[this.product()?.categories[0]] : ''
  }

  getEbookLang() {
    if(this.product()?.categories.length === 1) return this.product().categories[0]
    return this.product()?.categories.filter(category =>
      category !== 'English' && category !== 'Teacher')[0]
  }

  getLanguageFilter(){
    let lang = this.getEbookLang()
    if(lang) return CourseFilter.filters.find(filter => filter.category == lang).filter
    else return ''
  }

  getEbookDescription() {
    return this.product()?.description
  }

  getEbookCover() {
    return this.ebookCover
  }

  getAudioSize() {
    return { size: this.bookDetails.getAudioSize(this.localState)}
  }

  getDictationSize() {
    return { size: this.bookDetails.getDictationSize(this.localState)}
  }

  hasProduct() {
    return this.hasProductFlag
  }

  hasReaderPath() {
    return !this.entry || !!this.product()?.path
  }

  goToEbooksListWithFilter(language: string) {
    this.router.navigate(
      ['../'],
      {
        relativeTo: this.route,
        queryParams: { language: `${language}`},
        queryParamsHandling: 'merge'
      }
    )
  }

  goToDownload(app: string) {
    if(app==='android')
      return 'https://callan.co.uk/downloads/callan-method-app-3.9.2.apk'
    else if(app==='iOS')
      return 'https://itunes.apple.com/gb/app/callan-method-app/id1099785360'
    else
      return ''
  }

  isDownloading() {
    return this.downloadProgress !== null
  }

  isNative() {
    return this.isAppNative
  }

  openEBook(){
    this.router.navigate([`./reader`], {
      relativeTo: this.route
    })
  }
}
